import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { favoritesApi } from '@/lib/api';

const FavoritesContext = createContext(null);

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('favorites_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('favorites_session_id', sessionId);
  }
  return sessionId;
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const sessionId = getSessionId();

  const loadFavorites = useCallback(async () => {
    try {
      const [favResponse, countResponse] = await Promise.all([
        favoritesApi.getAll(sessionId),
        favoritesApi.count(sessionId)
      ]);
      setFavorites(favResponse.data);
      setFavoriteIds(new Set(favResponse.data.map(car => car.id)));
      setCount(countResponse.data.count);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const addFavorite = async (carId) => {
    try {
      await favoritesApi.add(carId, sessionId);
      setFavoriteIds(prev => new Set([...prev, carId]));
      setCount(prev => prev + 1);
      // Reload full list
      const response = await favoritesApi.getAll(sessionId);
      setFavorites(response.data);
      return true;
    } catch (error) {
      console.error('Error adding favorite:', error);
      return false;
    }
  };

  const removeFavorite = async (carId) => {
    try {
      await favoritesApi.remove(sessionId, carId);
      setFavoriteIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(carId);
        return newSet;
      });
      setCount(prev => Math.max(0, prev - 1));
      setFavorites(prev => prev.filter(car => car.id !== carId));
      return true;
    } catch (error) {
      console.error('Error removing favorite:', error);
      return false;
    }
  };

  const toggleFavorite = async (carId) => {
    if (favoriteIds.has(carId)) {
      return await removeFavorite(carId);
    } else {
      return await addFavorite(carId);
    }
  };

  const isFavorite = (carId) => {
    return favoriteIds.has(carId);
  };

  const value = {
    favorites,
    count,
    loading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    refresh: loadFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
